import { Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';

import { ActionText } from '@components/action-text';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { FormLabel } from '@components/form-label';
import { IconAddPlus } from '@components/icons/add-plus';
import { IconBrandOneSystems } from '@components/icons/brand-onesystems';
import { IconSend } from '@components/icons/send';
import { IntegrationCard } from '@components/integration-card';
import { ProgressBar } from '@components/progress-bar';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { abbreviateDate } from '@utils/date';
import { isRequestError } from '@utils/requests';

import { Loading } from 'components/Loading';
import { useModal } from 'features/modals';
import { useStore } from 'models/Provider';
import { IJob, IUser } from 'models/types';

import {
  IncompleteApplicationTransfer,
  OneCRMApplicationCasesTransfer,
  OneCRMJobCase,
  createApplicationCases,
  createOneCRMJobCase,
  loadApplicationCases,
  loadOneCRMJobCase,
  retryApplicationTransfer,
} from './api';
import { FileStatus } from './file-status';

export function OneCRMJobPage() {
  const { jobStore, t } = useStore();
  const [refetch, setRefetch] = useState(false);
  const { jobID } = useParams();
  const job = jobStore.items.get(jobID);
  const jobCaseQuery = useQuery({
    queryKey: ['onecrm-job-case', String(jobID)],
    queryFn: () => loadOneCRMJobCase(Number(jobID!)),
    refetchInterval: refetch ? 5000 : false,
    onSuccess(data) {
      if (!data.case) {
        setRefetch(false);
        return;
      }
      const { infoFileUploadStatus, notesFileUploadStatus, filesUploadStatus } = data.case;
      if (
        infoFileUploadStatus === 'P' ||
        notesFileUploadStatus === 'P' ||
        filesUploadStatus === 'P'
      ) {
        setRefetch(true);
      } else {
        setRefetch(false);
      }
    },
  });

  if (jobCaseQuery.isLoading || !jobCaseQuery.data) return <Loading />;

  return (
    <Stack space={6} orientation="vertical">
      <IntegrationCard
        icon={IconBrandOneSystems}
        header="OneCRM"
        text={t('integrations.onecrm.job.header')}
      />
      <AnimatePresence exitBeforeEnter initial={false}>
        <motion.div
          key={String(jobCaseQuery.data.case !== null)}
          animate={{ opacity: 1, x: 0 }}
          initial={{ opacity: 0, x: 40 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.15 }}
        >
          {jobCaseQuery.data.case ? (
            <JobCaseCreated jobCase={jobCaseQuery.data.case} />
          ) : (
            <CreateJobCase job={job} />
          )}
        </motion.div>
      </AnimatePresence>

      <ApplicationCases job={job} isDisabled={jobCaseQuery.data.case === null} />
    </Stack>
  );
}

function ApplicationCases({ job, isDisabled }: { job: IJob; isDisabled: boolean }) {
  const [refetch, setRefetch] = useState(false);
  const [showProgress, setShowProgress] = useState(false);

  const applicationCasesQuery = useQuery({
    queryKey: ['onecrm-application-cases', String(job.id)],
    queryFn: () => loadApplicationCases(job.id),
    enabled: !isDisabled,
    refetchInterval: refetch ? 5000 : false,
    onSuccess(data) {
      if (data.transferCompleted) {
        setRefetch(false);

        setTimeout(() => {
          setShowProgress(false);
        }, 2000);
        return;
      } else if (data.transferStarted) {
        setRefetch(true);
      }

      setShowProgress(true);
    },
  });

  return (
    <Fragment>
      {!applicationCasesQuery.isLoading && !applicationCasesQuery.data?.transferStarted && (
        <ApplicantData isDisabled={isDisabled} job={job} />
      )}
      {applicationCasesQuery.data && (
        <AnimatePresence exitBeforeEnter initial={false}>
          <motion.div
            key={String(showProgress) + String(applicationCasesQuery.data?.transferStarted)}
            animate={{ opacity: 1, x: 0 }}
            initial={{ opacity: 0, x: 40 }}
            exit={{ opacity: 0, x: -40 }}
            transition={{ duration: 0.15 }}
          >
            {applicationCasesQuery.data.transferCompleted && !showProgress ? (
              <TransferCompleted data={applicationCasesQuery.data} />
            ) : applicationCasesQuery.data.transferStarted ? (
              <TransferInProgress data={applicationCasesQuery.data} />
            ) : null}
          </motion.div>
        </AnimatePresence>
      )}
    </Fragment>
  );
}

function TransferInProgress({ data }: { data: OneCRMApplicationCasesTransfer }) {
  const { t } = useStore();
  const completed = data.totalCompleted;
  const total = data.totalApplications;
  const isDone = completed === total;

  return (
    <Card variant="default" space={6}>
      <Stack space={4}>
        <Text font="bold16" text={t('integrations.onecrm.job.applicants.header')} />
        <Stack space={2}>
          <AnimatePresence exitBeforeEnter initial={false}>
            <motion.div
              key={String(isDone)}
              animate={{ opacity: 1, y: 0 }}
              initial={{ opacity: 0, y: 10 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.15 }}
            >
              {isDone ? (
                <Text
                  font="regular16"
                  color="purple100"
                  text={t('integrations.onecrm.job.applicants.transfercomplete')}
                />
              ) : (
                <Text
                  font="regular16"
                  color="purple100"
                  text={`${t(
                    'integrations.onecrm.job.applicants.transfering',
                  )} • ${completed} / ${total}`}
                />
              )}
            </motion.div>
          </AnimatePresence>
          <ProgressBar ratio={completed / total} />
        </Stack>
      </Stack>
    </Card>
  );
}

function TransferCompleted({ data }: { data: OneCRMApplicationCasesTransfer }) {
  const { t } = useStore();
  if (data.success) return <TransferCompletedSuccess data={data} />;

  return (
    <div>
      <Card variant="default" space={6}>
        <Stack space={6}>
          <Text font="bold16" text={t('integrations.onecrm.job.applicants.header')} />
          <Stack space={4}>
            {data.incompleteApplications.map((applicationCase) => (
              <IncompleteApplication data={applicationCase} key={applicationCase.id} />
            ))}
          </Stack>
        </Stack>
      </Card>
    </div>
  );
}

function IncompleteApplication({ data }: { data: IncompleteApplicationTransfer }) {
  const { t } = useStore();
  const { showError, showSuccess } = useToaster();

  const queryClient = useQueryClient();

  const retryMutation = useMutation(retryApplicationTransfer, {
    onSuccess() {
      showSuccess(t('integrations.onecrm.applicants.retry.success'));
      queryClient.invalidateQueries(['onecrm-application-cases']);
    },
    onError(data) {
      if (
        !isRequestError(data) ||
        (isRequestError(data) && data.response?.status !== 400) ||
        !data.response?.data
      ) {
        showError(t('integrations.onecrm.applicants.retry.fail'));
        return;
      }

      showError(t('integrations.onecrm.applicants.retry.fail') + ': ' + data.response.data);
    },
  });

  return (
    <div style={{ position: 'relative' }}>
      {retryMutation.isLoading && <Loading />}
      <Text font="semibold14" color="red200" text={t('integrations.onecrm.transfer.e')} />
      <Text text={data.name} />
      <Text style={{ wordBreak: 'break-word' }} text={data.errorMessage} />
      <ActionText
        color="purple60"
        isDisabled={retryMutation.isLoading}
        onClick={() => retryMutation.mutate(data.id)}
      >
        {t('integrations.onecrm.applicants.retry.button')}
      </ActionText>
    </div>
  );
}

function TransferCompletedSuccess({ data }: { data: OneCRMApplicationCasesTransfer }) {
  const { t, lang } = useStore();
  return (
    <Card variant="default" space={6}>
      <Stack space={6}>
        <Text font="bold16" text={t('integrations.onecrm.job.applicants.header')} />
        <Stack space={4}>
          <Stack space={1}>
            <Text font="semibold14" text={t('integrations.onecrm.job.applicants.cv')} />
            <FileStatus status="S" />
          </Stack>
          <Stack space={1}>
            <Text font="semibold14" text={t('integrations.onecrm.job.applicants.cover')} />
            <FileStatus status="S" />
          </Stack>
          <Stack space={1}>
            <Text font="semibold14" text={t('integrations.onecrm.job.applicants.files')} />
            <FileStatus status="S" />
          </Stack>
          <Stack space={1}>
            <Stack space={2} orientation="horizontal" align="center">
              <Text font="semibold14" text={t('integrations.onecrm.job.applicants.qa')} />
              <Text font="regular14" color="purple50" text="PDF" />
            </Stack>
            <FileStatus status="S" />
          </Stack>
          <Stack space={1}>
            <Stack space={2} orientation="horizontal" align="center">
              <Text font="semibold14" text={t('integrations.onecrm.job.applicants.msg')} />
              <Text font="regular14" color="purple50" text="PDF" />
            </Stack>
            <FileStatus status="S" />
          </Stack>
        </Stack>
        {data.transferredBy && (
          <Fragment>
            <Separator />
            <Stack space={1}>
              <Text font="semibold14" text={t('integrations.onecrm.job.applicants.created')} />
              <div>
                <Text font="regular16" text={data.transferredBy.name} />
                {data.transferCompleted && (
                  <Text text={abbreviateDate(data.transferCompleted, lang)} />
                )}
              </div>
            </Stack>
          </Fragment>
        )}
      </Stack>
    </Card>
  );
}

function ApplicantData({ isDisabled, job }: { isDisabled: boolean; job: IJob }) {
  const { t } = useStore();
  const { showModal } = useModal();

  const queryClient = useQueryClient();
  const uploadMutation = useMutation(createApplicationCases, {
    onSuccess() {
      queryClient.invalidateQueries(['onecrm-application-cases', String(job.id)]);
    },
  });

  useEffect(() => {
    job.loadApplicationsCount();
  }, [job]);

  function onSubmit() {
    // Since ConfirmModal isn't typed, here we don't know `negative` isn't a required prop.
    // @ts-expect-error
    showModal('CONFIRM_MODAL', {
      confirm: (id: number) => {
        uploadMutation.mutate(id);
        return true;
      },
      data: {
        id: job.id,
      },
      buttonIcon: <IconSend />,
      headerText: t('integrations.onecrm.applicants.confirm.header'),
      mainText: t('integrations.onecrm.applicants.confirm.main', job.numApplications),
      confirmText: t('integrations.onecrm.applicants.confirm.button'),
    });
  }

  return (
    <Card space={6} variant="transparent">
      {uploadMutation.isLoading && <Loading delay={500} />}
      <Stack space={6}>
        <Text font="bold16" text={t('integrations.onecrm.job.applicants.header')} />
        <Stack space={2}>
          <Text font="semibold14" text={t('integrations.onecrm.data_to_be_sent')} />
          <Text text={t('integrations.onecrm.job.applicants.cv')} />
          <Text text={t('integrations.onecrm.job.applicants.cover')} />
          <Text text={t('integrations.onecrm.job.applicants.files')} />
          <Text text={t('integrations.onecrm.job.applicants.qa') + ' (PDF)'} />
          <Text text={t('integrations.onecrm.job.applicants.msg') + ' (PDF)'} />
        </Stack>
        <Stack align="flex-end" space={2}>
          <Button
            variant="primary"
            isDisabled={isDisabled || uploadMutation.isLoading}
            onClick={onSubmit}
            iconLeft={<IconSend />}
            text={t('integrations.onecrm.job.applicants.send')}
          />
        </Stack>
      </Stack>
    </Card>
  );
}

function JobCaseCreated({ jobCase }: { jobCase: OneCRMJobCase }) {
  const { t } = useStore();
  return (
    <Card variant="default" space={6}>
      <Stack space={6}>
        <Text font="bold16" text={t('integrations.onecrm.jobcase.header')} />
        <Stack space={4}>
          <Stack space={1}>
            <Text font="semibold14" text={t('integrations.onecrm.jobcase.title')} />
            <Text font="regular16" text={jobCase.caseName} />
          </Stack>
          <Stack space={1}>
            <Text font="semibold14" text={t('integrations.onecrm.jobcase.manager')} />
            <Text font="regular16" text={jobCase.manager.name} />
          </Stack>
        </Stack>
        <Separator />
        <Stack space={4}>
          <Stack space={1}>
            <Stack space={2} orientation="horizontal" align="center">
              <Text font="semibold14" text={t('integrations.onecrm.jobcase.info')} />
              <Text font="regular14" color="purple50" text="PDF" />
            </Stack>
            <FileStatus status={jobCase.infoFileUploadStatus} />
          </Stack>
          <Stack space={1}>
            <Stack space={2} orientation="horizontal" align="center">
              <Text font="semibold14" text={t('integrations.onecrm.jobcase.notes')} />
              <Text font="regular14" color="purple50" text="PDF" />
            </Stack>
            <FileStatus status={jobCase.notesFileUploadStatus} />
          </Stack>
          <Stack space={1}>
            <Text font="semibold14" text={t('integrations.onecrm.jobcase.files')} />
            <FileStatus status={jobCase.filesUploadStatus} />
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
}

function CreateJobCase({ job }: { job: IJob }) {
  const [managerId, setManagerId] = useState<string>();
  const { t } = useStore();
  const { showSuccess, showError } = useToaster();
  const queryClient = useQueryClient();

  const createJobCaseMutation = useMutation(createOneCRMJobCase, {
    onSuccess() {
      showSuccess(t('integrations.onecrm.jobcase.create.success'));
      job.loadIntegrations();
      queryClient.invalidateQueries(['onecrm-job-case', String(job.id)]);
      queryClient.invalidateQueries(['onecrm-application-cases', String(job.id)]);
    },
    onError(data) {
      if (
        !isRequestError(data) ||
        (isRequestError(data) && data.response?.status !== 400) ||
        !data.response?.data
      ) {
        showError(t('integrations.onecrm.jobcase.create.error'));
        return;
      }

      showError(data.response.data);
    },
  });

  const team = job.followers.map((f: IUser) => ({
    value: f.id,
    label: f.name,
  }));

  return (
    <Card variant="transparent" space={6}>
      {createJobCaseMutation.isLoading && <Loading />}
      <Stack space={6}>
        <Text font="bold16" text={t('integrations.onecrm.jobcase.header')} />
        <Stack space={4}>
          <Select
            isDisabled={createJobCaseMutation.isLoading}
            options={team}
            onChange={setManagerId}
            label={t('integrations.onecrm.jobcase.manager')}
          />
          <div>
            <FormLabel text={t('integrations.onecrm.data_to_be_sent')} />
            <Text font="regular16" text={t('integrations.onecrm.jobcase.info') + ' (PDF)'} />
          </div>
        </Stack>
        <Stack space={2} align="flex-end">
          <Button
            isDisabled={createJobCaseMutation.isLoading}
            iconLeft={<IconAddPlus />}
            variant="primary"
            text={t('integrations.onecrm.jobcase.create.submit')}
            onClick={() =>
              createJobCaseMutation.mutate({ jobId: job.id, userId: Number(managerId) })
            }
          />
        </Stack>
      </Stack>
    </Card>
  );
}
